import React from 'react'
import { Button, CircularProgress } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { ApiPushCancelCampaign, ApiPushExportCampaign, ApiPushRetryFailed } from '../../apiService/Core/Apicall'
import PopupDialog from '../Popups/ConfirmDialog'
import { dangerBtnSx, headerBtnSx } from './pushStyles'
import { getErrorMessage } from './pushUtils'

interface BaseProps {
    campaignId: string
    onDone?: () => void
}

/** Retry failed devices. The backend is idempotent: devices already accepted are never re-sent. */
export const RetryFailedButton: React.FC<BaseProps & { disabled?: boolean }> = ({ campaignId, disabled, onDone }) => {
    const [open, setOpen] = React.useState(false)
    const [busy, setBusy] = React.useState(false)
    const busyRef = React.useRef(false)

    const onConfirm = async () => {
        if (busyRef.current) return
        busyRef.current = true
        setBusy(true)
        try {
            const res = await ApiPushRetryFailed(campaignId)
            const d = res?.data
            toast.success(`Requeued ${d?.requeuedUsers ?? 0} users (${d?.requeuedDevices ?? 0} devices). Users already accepted are never re-sent.`)
            setOpen(false)
            onDone?.()
        } catch (e) {
            toast.error(getErrorMessage(e))
            setOpen(false)
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }

    return (
        <>
            <Button variant="outlined" disabled={disabled || busy} onClick={() => setOpen(true)} sx={headerBtnSx}>
                Retry failed
            </Button>
            <PopupDialog
                open={open}
                onClose={() => setOpen(false)}
                onConfirm={onConfirm}
                loading={busy}
                variant="warning"
                title="Retry failed notifications"
                message="Failed devices will be queued again. Devices that Firebase already accepted are not sent again, and permanently invalid tokens are not retried."
                confirmText="Retry failed"
            />
        </>
    )
}

/**
 * Opens New campaign pre-filled from this campaign. Nothing is sent from here: the admin reviews the live audience
 * preview and confirms, which creates a brand new campaign (new idempotency key), so nobody is double sent by accident.
 */
export const ReuseCampaignButton: React.FC<{ campaignId: string; disabled?: boolean }> = ({ campaignId, disabled }) => {
    const navigate = useNavigate()
    return (
        <Button variant="outlined" disabled={disabled} onClick={() => navigate(`/admin/notifications/new?reuse=${encodeURIComponent(campaignId)}`)} sx={headerBtnSx}>
            Reuse &amp; resend
        </Button>
    )
}

export const CancelCampaignButton: React.FC<BaseProps> = ({ campaignId, onDone }) => {
    const [open, setOpen] = React.useState(false)
    const [busy, setBusy] = React.useState(false)
    const busyRef = React.useRef(false)

    const onConfirm = async () => {
        if (busyRef.current) return
        busyRef.current = true
        setBusy(true)
        try {
            await ApiPushCancelCampaign(campaignId)
            toast.success('Campaign cancellation requested')
            setOpen(false)
            onDone?.()
        } catch (e) {
            toast.error(getErrorMessage(e))
            setOpen(false)
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }

    return (
        <>
            <Button variant="contained" disabled={busy} onClick={() => setOpen(true)} sx={dangerBtnSx}>
                Cancel campaign
            </Button>
            <PopupDialog
                open={open}
                onClose={() => setOpen(false)}
                onConfirm={onConfirm}
                loading={busy}
                variant="danger"
                title="Cancel campaign"
                message="Remaining notifications will not be sent. Notifications already accepted by Firebase cannot be recalled."
                confirmText="Cancel campaign"
                cancelText="Keep running"
            />
        </>
    )
}

/** Downloads the recipient report through the authenticated axios client (a plain link would be unauthenticated). */
export const ExportButton: React.FC<{ campaignId: string }> = ({ campaignId }) => {
    const [busy, setBusy] = React.useState(false)

    const download = async () => {
        setBusy(true)
        try {
            const res = await ApiPushExportCampaign(campaignId)
            const disposition: string = res?.headers?.['content-disposition'] || ''
            const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
            const filename = match ? decodeURIComponent(match[1]) : `push-campaign-${campaignId}.xlsx`
            const url = window.URL.createObjectURL(new Blob([res.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            if (res?.headers?.['x-export-truncated'] === 'true') {
                toast.warn('The export was truncated to the maximum number of rows.')
            }
        } catch (e) {
            toast.error(getErrorMessage(e))
        } finally {
            setBusy(false)
        }
    }

    return (
        <Button
            variant="outlined"
            onClick={download}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DownloadIcon />}
            sx={headerBtnSx}
        >
            {busy ? 'Preparing…' : 'Export to Excel'}
        </Button>
    )
}
